import React, { useContext, useEffect, useState, type ReactNode } from 'react';
import {
  Alert,
  Backdrop,
  Box,
  CircularProgress,
  CssBaseline,
  Snackbar,
} from '@mui/material';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact, {
  type BugsnagErrorBoundary,
} from '@bugsnag/plugin-react';
import BugsnagPerformance from '@bugsnag/browser-performance';

import pkg from '../../package.json';
import AppTheme from '../shared-theme/AppTheme';
import {
  chartsCustomizations,
  dataGridCustomizations,
  // datePickersCustomizations,
  treeViewCustomizations,
} from '../theme/customizations';
import { DataContext } from '../contexts/DataContext';
import CustomizedDialog from '../components/CustomizedDialog';
import { BackdropContextProvider } from '../contexts/BackdropContext';
import { AuthContext } from '../contexts/AuthContext';
import Loading from '../pages/Loading';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  // ...datePickersCustomizations,
  ...treeViewCustomizations,
};

export default function ServicesWrapper({
  children,
  ...props
}: Readonly<{ children?: React.ReactNode }>) {
  const dataCtx = useContext(DataContext);
  const authCtx = useContext(AuthContext);
  const [bugsnagStarted, setBugsnagStarted] = useState<boolean>(false);
  const [bugsnagLoaded, setBugsnagLoaded] = useState<boolean>(false);

  console.log('ServicesWrapper rendering');

  const appComponent = (
    <AppTheme
      {...props}
      disableCustomTheme={false}
      themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <BackdropContextProvider>
        <Backdrop
          sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })}
          open={dataCtx.backdropOpen}>
          <CircularProgress color="inherit" />
        </Backdrop>
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          open={dataCtx.snackbarOpen}
          autoHideDuration={5000}
          onClose={dataCtx.handleSnackbarClose}>
          <Alert
            onClose={dataCtx.handleSnackbarClose}
            severity={dataCtx.snackbarSeverity}
            variant="filled"
            sx={{ width: '100%' }}>
            {dataCtx.snackbarMessage}
          </Alert>
        </Snackbar>
        <CustomizedDialog />
        {children}
      </BackdropContextProvider>
    </AppTheme>
  );

  // return appComponent;

  useEffect(() => {
    if (bugsnagStarted) return;
    // Bugsnag.start must be called in useEffect, otherwise authCtx.userEmail and others data haven't load yet
    Bugsnag.start({
      apiKey: import.meta.env.VITE_BUGSNAG_KEY,
      plugins: [new BugsnagPluginReact()],
      appVersion: pkg.version,
      onError: function (event) {
        console.log(`BugSnag error fired, user email: ${authCtx.userEmail}`);
        event.setUser(
          authCtx.merchantId?.toString(),
          authCtx.userEmail,
          authCtx.merchantName,
        );
      },
    });
    BugsnagPerformance.start({ apiKey: import.meta.env.VITE_BUGSNAG_KEY });

    const run = async () => {
      let i = 0;

      while (i <= 10) {
        if (Bugsnag.getPlugin('react') !== undefined) {
          setBugsnagLoaded(true);
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, 1000));
        i++;
      }
      setBugsnagLoaded(true);
    };

    run();

    setBugsnagStarted(true);
  }, []);

  if (!bugsnagLoaded) {
    return <Loading />;
  }

  if (Bugsnag.getPlugin('react') === undefined) {
    console.log('BugSnag undefined');
    return appComponent;
  }

  const ErrorBoundary = Bugsnag.getPlugin('react')!.createErrorBoundary(React);

  console.log('BugSnag loaded');

  return <ErrorBoundary>{appComponent}</ErrorBoundary>;
}
